import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import * as echarts from 'echarts';
import { withinRange } from '../trendRange';
import type { ECharts, EChartsOption } from 'echarts';
import type { TrendWidget, TrendSeries } from '../widgets';
import type { IPointBindingSource, HistoryAggregation, PointHistorySample, PointValue } from '../binding';
import { pointSignal } from '../pointSignal';
import { formatNumber } from '../../format/leafValue';
import { smoothForDisplay, type SeriesPoint } from '../../api/history';
import './TrendRenderer.css';

interface Props {
  widget: TrendWidget;
  source: IPointBindingSource;
}

interface RangeChip { key: string; label: string; ms: number; }

const RANGES: RangeChip[] = [
  { key: '1h',  label: '1 h',   ms: 60 * 60_000 },
  { key: '6h',  label: '6 h',   ms: 6 * 60 * 60_000 },
  { key: '24h', label: '24 h',  ms: 24 * 60 * 60_000 },
  { key: '7d',  label: '7 T',   ms: 7 * 24 * 60 * 60_000 },
  { key: '30d', label: '30 T',  ms: 30 * 24 * 60 * 60_000 },
];

const PALETTE = [
  '#3b82f6', '#f59e0b', '#22c55e', '#ef4444', '#a855f7', '#0ea5e9', '#eab308',
];

const HISTORY_REFRESH_MS = 5 * 60_000;

interface SeriesTail {
  t: number;
  v: number | null;
}

/**
 * Full trend chart. ECharts-backed line chart with a time-range chip strip,
 * multi-series legend, drag-select zoom (double-click resets), crosshair
 * tooltip. Backed by history + live tail per series.
 *
 * History: `auto` (default) loads the shape — the M4 polyline of all raw
 * points at the chart's pixel width, pixel-identical at any window length.
 * The fixed levels `1m` … `1d` load exact interval means instead. Both are
 * drawn as step lines: a CoV value holds until the next report, a sloped line
 * between two reports would show values that never existed. `smooth` draws a
 * smoothed curve instead — a display option only (see smoothForDisplay).
 */
export function TrendRenderer({ widget, source }: Props) {
  const defaultRangeMs = parseIsoDurationMs(widget.defaultRange) ?? RANGES[2].ms;
  const initialKey = RANGES.find((r) => r.ms === defaultRangeMs)?.key ?? '24h';

  const [rangeKey, setRangeKey] = useState<string>(initialKey);
  const [customRangeMs, setCustomRangeMs] = useState<number | null>(
    RANGES.some((r) => r.ms === defaultRangeMs) ? null : defaultRangeMs,
  );
  const rangeMs = rangeKey === 'custom' && customRangeMs != null
    ? customRangeMs
    : (RANGES.find((r) => r.key === rangeKey)?.ms ?? defaultRangeMs);

  const [customOpen, setCustomOpen] = useState(false);

  const seriesList = widget.series ?? [];

  // History + live tail per series.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);

  const [tails, setTails] = useState<SeriesTail[][]>(() => seriesList.map(() => []));
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const lastAppendedTs = useRef<number[]>(seriesList.map(() => -Infinity));

  useEffect(() => {
    // Reset when the widget definition changes.
    lastAppendedTs.current = seriesList.map(() => -Infinity);
    setTails(seriesList.map(() => []));
    setHistoryLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.id, seriesList.length]);

  // Fetch history whenever the range changes; refresh periodically.
  useEffect(() => {
    if (seriesList.length === 0) return;
    let cancelled = false;
    const controller = new AbortController();
    const agg = pickAggregation(widget.defaultAggregation);

    const load = async () => {
      const until = new Date();
      const since = new Date(until.getTime() - rangeMs);
      const widthPx = containerRef.current?.clientWidth || undefined;
      try {
        const results = await Promise.all(
          seriesList.map((s) => source.getHistory(s.point, since, until, agg, controller.signal, widthPx)),
        );
        if (cancelled) return;
        const mapped = results.map((samples) =>
          samples
            .map(toTail)
            .filter((s): s is SeriesTail => s !== null)
            .sort((a, b) => a.t - b.t),
        );
        setTails(mapped);
        setHistoryLoaded(true);
        lastAppendedTs.current = mapped.map((s) => (s.length > 0 ? s[s.length - 1].t : -Infinity));
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[TrendRenderer] history fetch failed', {
          widgetId: widget.id,
          err,
        });
        if (!cancelled) setHistoryLoaded(true);
      }
    };

    void load();
    const timer = window.setInterval(load, HISTORY_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, widget.id, rangeMs, widget.defaultAggregation]);

  // Live-tail subscriptions per series. Signals are shared via pointSignal.
  const signals = useMemo(
    () => seriesList.map((s) => pointSignal(source, s.point)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, widget.id, seriesList.length, ...seriesList.map((s) => s.point)],
  );
  useEffect(() => {
    return () => {
      for (const sig of signals) sig.release();
    };
  }, [signals]);

  useEffect(() => {
    if (!(widget.liveTail ?? true)) return;
    const nowCutoff = Date.now() - rangeMs;
    let changed = false;
    const next = tails.map((series, idx) => {
      const live = signals[idx]?.value.value;
      if (!live) return series;
      const sample = liveToTail(live);
      if (!sample) return series;
      if (sample.t <= lastAppendedTs.current[idx]) return series;
      lastAppendedTs.current[idx] = sample.t;
      changed = true;
      const appended = [...series, sample];
      // Drop points older than the current window so the array doesn't grow
      // forever while the widget stays mounted on a long shift.
      let firstKept = 0;
      while (firstKept < appended.length && appended[firstKept].t < nowCutoff) firstKept++;
      return firstKept === 0 ? appended : appended.slice(firstKept);
    });
    if (changed) setTails(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, signals.map((s) => s.value.value));

  // Mount / dispose ECharts instance.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const inst = echarts.init(el);
    chartRef.current = inst;
    const dblclick = () => {
      inst.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
    };
    el.addEventListener('dblclick', dblclick);
    const ro = new ResizeObserver(() => inst.resize());
    ro.observe(el);
    return () => {
      el.removeEventListener('dblclick', dblclick);
      ro.disconnect();
      inst.dispose();
      chartRef.current = null;
    };
  }, []);

  // Push option updates whenever data / range change.
  useEffect(() => {
    const inst = chartRef.current;
    if (!inst) return;
    inst.setOption(buildOption(widget, seriesList, tails, rangeMs), { notMerge: false });
  }, [widget, seriesList, tails, rangeMs]);

  const isEmpty = historyLoaded && tails.every((t) => t.length === 0);

  return (
    <div class="hv-trend">
      <div class="hv-trend__header">
        {widget.label && <div class="hv-trend__label">{widget.label}</div>}
        <div class="hv-trend__ranges" role="tablist" aria-label="Zeitfenster">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={rangeKey === r.key}
              class={`hv-trend__chip${rangeKey === r.key ? ' hv-trend__chip--active' : ''}`}
              onClick={() => { setRangeKey(r.key); setCustomOpen(false); }}
            >
              {r.label}
            </button>
          ))}
          <button
            type="button"
            class={`hv-trend__chip hv-trend__chip--custom${rangeKey === 'custom' ? ' hv-trend__chip--active' : ''}`}
            onClick={() => setCustomOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={customOpen}
            title="Eigener Zeitraum"
          >
            {rangeKey === 'custom' && customRangeMs != null ? formatDurationMs(customRangeMs) : '…'}
          </button>
          {customOpen && (
            <CustomRangePopover
              onCancel={() => setCustomOpen(false)}
              onApply={(ms) => {
                setCustomRangeMs(ms);
                setRangeKey('custom');
                setCustomOpen(false);
              }}
            />
          )}
        </div>
      </div>

      <div class="hv-trend__chart-wrap">
        <div ref={containerRef} class="hv-trend__chart" />
        {isEmpty && <div class="hv-trend__empty">Keine Historie</div>}
        {!historyLoaded && <div class="hv-trend__empty">Lade Verlauf …</div>}
      </div>

      <div class="hv-trend__legend">
        {seriesList.map((s, idx) => (
          <span key={s.point} class="hv-trend__legend-item">
            <span class="hv-trend__swatch" style={{ background: seriesColour(s, idx) }} />
            {s.label ?? s.point}
            {/* Eine Linie ohne Werte sieht sonst wie ein Fehler der Anzeige aus,
                etwa wenn am Regler der Fuehler fehlt. */}
            {historyLoaded && !isEmpty && tails[idx]?.length === 0 && (
              <span class="hv-trend__legend-none">kein Messwert</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

// --------------- popover ---------------

function CustomRangePopover({ onApply, onCancel }:
  { onApply: (ms: number) => void; onCancel: () => void }) {
  const now = new Date();
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60_000);
  const [from, setFrom] = useState<string>(toLocalInput(dayAgo));
  const [to, setTo] = useState<string>(toLocalInput(now));

  const apply = () => {
    const fromMs = Date.parse(from);
    const toMs = Date.parse(to);
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return;
    onApply(toMs - fromMs);
  };

  return (
    <div class="hv-trend__popover" role="dialog" aria-label="Eigener Zeitraum">
      <label class="hv-trend__popover-field">
        <span>Von</span>
        <input type="datetime-local" value={from}
               onChange={(e) => setFrom((e.currentTarget as HTMLInputElement).value)} />
      </label>
      <label class="hv-trend__popover-field">
        <span>Bis</span>
        <input type="datetime-local" value={to}
               onChange={(e) => setTo((e.currentTarget as HTMLInputElement).value)} />
      </label>
      <div class="hv-trend__popover-actions">
        <button type="button" class="hv-trend__btn" onClick={onCancel}>Abbrechen</button>
        <button type="button" class="hv-trend__btn hv-trend__btn--primary" onClick={apply}>Übernehmen</button>
      </div>
    </div>
  );
}

// --------------- helpers ---------------

function toTail(sample: PointHistorySample): SeriesTail | null {
  const t = Date.parse(sample.timestamp);
  if (!Number.isFinite(t)) return null;
  return {
    t,
    v: sample.value != null && Number.isFinite(sample.value) ? sample.value : null,
  };
}

function liveToTail(v: PointValue): SeriesTail | null {
  const t = Date.parse(v.timestamp);
  if (!Number.isFinite(t)) return null;
  const num = typeof v.value === 'number' && Number.isFinite(v.value) ? v.value : null;
  return { t, v: num };
}

function seriesColour(s: TrendSeries, idx: number): string {
  return s.colour ?? PALETTE[idx % PALETTE.length];
}

function pickAggregation(defaultAgg: string | undefined): HistoryAggregation {
  return defaultAgg && defaultAgg !== 'auto' ? defaultAgg as HistoryAggregation : 'auto';
}

function parseIsoDurationMs(iso: string | undefined): number | null {
  if (!iso) return null;
  // Accept the C# TimeSpan JSON shape "hh:mm:ss" or "d.hh:mm:ss" as well as
  // an ISO 8601 duration "PT24H". We keep the parser deliberately narrow.
  const tsMatch = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/.exec(iso);
  if (tsMatch) {
    const days = parseInt(tsMatch[1] ?? '0', 10);
    const h = parseInt(tsMatch[2], 10);
    const m = parseInt(tsMatch[3], 10);
    const s = parseInt(tsMatch[4], 10);
    return ((days * 24 + h) * 3600 + m * 60 + s) * 1000;
  }
  const isoMatch = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(iso);
  if (isoMatch) {
    const d = parseInt(isoMatch[1] ?? '0', 10);
    const h = parseInt(isoMatch[2] ?? '0', 10);
    const m = parseInt(isoMatch[3] ?? '0', 10);
    const s = parseInt(isoMatch[4] ?? '0', 10);
    return ((d * 24 + h) * 3600 + m * 60 + s) * 1000;
  }
  return null;
}

function formatDurationMs(ms: number): string {
  const totalMin = Math.round(ms / 60_000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.round(totalMin / 60);
  if (h < 48) return `${h} h`;
  const d = Math.round(h / 24);
  return `${d} T`;
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function areaFill(colour: string, opacity = 0.4): Record<string, unknown> {
  if (!/^#[0-9a-fA-F]{6}$/.test(colour)) return {};
  const top = Math.round(Math.min(1, Math.max(0, opacity)) * 255).toString(16).padStart(2, '0');
  return {
    color: {
      type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
      colorStops: [{ offset: 0, color: `${colour}${top}` }, { offset: 1, color: `${colour}00` }],
    },
  };
}

function buildOption(
  widget: TrendWidget,
  seriesList: TrendSeries[],
  tails: SeriesTail[][],
  rangeMs: number,
): EChartsOption {
  const now = Date.now();
  const from = now - rangeMs;

  // Group series by axisGroup — one Y-axis per distinct group so different
  // physical quantities don't get squished onto the same scale.
  const axisKeys: string[] = [];
  const axisIndexFor = (s: TrendSeries): number => {
    const key = s.axisGroup ?? 'default';
    let i = axisKeys.indexOf(key);
    if (i < 0) { i = axisKeys.length; axisKeys.push(key); }
    return i;
  };

  const echartsSeries: EChartsOption['series'] = seriesList.map((s, idx) => {
    const yAxisIndex = axisIndexFor(s);
    const style = s.style ?? 'line';
    const points: SeriesPoint[] = tails[idx]?.map((p) => [p.t, withinRange(p.v, s)]) ?? [];
    // Lines are steps unless smoothing is chosen (see the component comment);
    // 'stepAfter' is always a step, bars have no connecting line.
    const smoothed = s.smooth === true && style !== 'bars' && style !== 'stepAfter';
    const stepped = style !== 'bars' && !smoothed;
    const data = smoothed ? smoothForDisplay(points) : points;
    return {
      name: s.label ?? s.point,
      type: style === 'bars' ? ('bar' as const) : ('line' as const),
      showSymbol: false,
      smooth: smoothed ? 0.35 : false,
      step: stepped ? ('end' as const) : (false as const),
      // Flaeche mit Verlauf nach unten, wie in der TP8-Demo; nur bei #rrggbb.
      // fill macht sie zart (Kutiak, 18.10.2026: feine Linien, zarte Fuellung).
      areaStyle: style === 'area' || (s.fill ?? 0) > 0 ? areaFill(seriesColour(s, idx), s.fill ?? 0.4) : undefined,
      yAxisIndex,
      itemStyle: { color: seriesColour(s, idx) },
      lineStyle: { color: seriesColour(s, idx), width: s.width ?? 1.5 },
      data,
    };
  });

  // At least one y axis: ECharts throws ("getAxesOnZeroOf is not a
  // function") on an empty list, which happens for a trend that has no
  // curve yet, e.g. right after it was dropped in the editor.
  if (axisKeys.length === 0) axisKeys.push('default');
  const yAxes = axisKeys.map((key, i) => ({
    type: 'value' as const,
    position: (i === 0 ? 'left' : 'right') as 'left' | 'right',
    name: key === 'default' ? '' : key,
    scale: true,
    splitLine: { lineStyle: { opacity: 0.15 } },
  }));

  // Ohne Suchbalken (zoomBar: false, am Panel) bleibt das Zoomen per Wischen.
  const zoomBar = widget.zoomBar !== false;
  return {
    animation: false,
    grid: { left: 48, right: axisKeys.length > 1 ? 48 : 16, top: 12, bottom: zoomBar ? 40 : 24 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', animation: false },
      valueFormatter: (v) => typeof v === 'number' ? formatNumber(v, 2) : String(v ?? '—'),
    },
    xAxis: {
      type: 'time',
      min: from,
      max: now,
      splitLine: { show: false },
    },
    yAxis: yAxes,
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        filterMode: 'none',
      },
      ...(zoomBar ? [{
        type: 'slider' as const,
        xAxisIndex: 0,
        height: 18,
        bottom: 6,
        filterMode: 'none' as const,
        brushSelect: true,
      }] : []),
    ],
    series: echartsSeries,
    useUTC: false,
    aria: { label: { description: widget.label ?? 'Trend' } },
  };
}
